import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { Tooltip } from '@mui/material';
import { SegmentedControl } from 'new/elements/SegmentControl';
import { JSONView } from 'new/elements/JSONView/JSONView';
import { EmptyView } from 'new/elements/EmptyView';
import { ReactComponent as TableIcon } from 'new/components/icons/fi-rr-table.svg';
import { ReactComponent as JsonIcon } from 'new/components/icons/icon-rest-api.svg';
import { ReactComponent as RefreshIcon } from 'new/components/icons/fi-rr-refresh-no-border.svg';
import { ReactComponent as DownloadIcon } from 'new/components/icons/fi-rr-download.svg';
import { ReactComponent as RegularIcon } from 'new/components/icons/view-standard.svg';
import { ReactComponent as TransposeIcon } from 'new/components/icons/view-transpose.svg';
import type { SchemaColumn } from './SchemaTreePanel';

const LAYOUT_CONTROLS = [
  { value: 'horizontal', label: 'Standard', icon: <RegularIcon /> },
  { value: 'transposed', label: 'Transposed', icon: <TransposeIcon /> },
];

const VIEW_CONTROLS = [
  { value: 'table', label: 'Table', icon: <TableIcon /> },
  { value: 'json', label: 'JSON', icon: <JsonIcon /> },
];

const PREVIEW_TOGGLE_SX = {
  '& .MuiTab-root': {
    minHeight: '26px',
    paddingTop: '3px',
    paddingBottom: '3px',
  },
};

type SampleResponse = {
  data?: Array<Record<string, unknown>>;
  columns?: string[];
};

export type DatabaseSampleData = {
  response?: SampleResponse;
  errorCode?: number;
  statusCode?: number;
  error?: string;
  errorMessage?: string;
} | null;

type Props = {
  columns: SchemaColumn[];
  samples: DatabaseSampleData;
  loading: boolean;
  tableLabel?: string;
  canFetch: boolean;
  onFetch: () => void;
  /** 'Default' = Table mode, 'Query' = Query mode (samples come from Run). */
  mode: 'Default' | 'Query';
  /** Optional supplementary context shown below the error message when a run fails. */
  errorHint?: React.ReactNode;
};

type Layout = 'horizontal' | 'transposed';

const MIN_COL = 150;
const MAX_COL = 380;
const DEFAULT_COL = 210;
const SKELETON_TRANSPOSED_COLS = 5;
const SKELETON_HORIZONTAL_ROWS = 6;
// At/under this column count a regular (horizontal) grid reads better; above it
// the transposed view avoids squeezing many columns into the width.
const HORIZONTAL_MAX_COLS = 8;

// Some backends serialize sample values with ASCII-only escaping, so non-ASCII
// characters arrive as literal "\uXXXX" sequences (e.g. a degree sign in
// lat/long values). Decode them for display so they render as real glyphs.
const decodeUnicodeEscapes = (s: string): string =>
  s.includes('\\u')
    ? s.replace(/\\u([0-9a-fA-F]{4})/g, (_m, hex) => String.fromCharCode(parseInt(hex, 16)))
    : s;

// Only longer values can overflow the clamped 3-line cell; short ones skip the
// measurement + tooltip entirely.
const TOOLTIP_CANDIDATE_LEN = 60;

const FULL_VALUE_TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      maxWidth: 380,
      bgcolor: 'var(--color-greyscale-1000)',
      p: '8px 10px',
    },
  },
};

const PreviewCell: React.FC<{ value: unknown }> = ({ value }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [truncated, setTruncated] = useState(false);

  const isNull = value === null || value === undefined;
  const raw = isNull ? '' : typeof value === 'string' ? value : JSON.stringify(value);
  const text = decodeUnicodeEscapes(raw);
  const candidate = !isNull && text.length > TOOLTIP_CANDIDATE_LEN;

  useLayoutEffect(() => {
    if (!candidate) return;
    const el = ref.current;
    if (el) setTruncated(el.scrollHeight - el.clientHeight > 1);
  }, [candidate, text]);

  const inner = (
    <div ref={ref} className="db-preview-value-text">
      {isNull ? <span className="db-preview-null">null</span> : text}
    </div>
  );

  if (candidate && truncated) {
    return (
      <Tooltip
        placement="top-start"
        enterDelay={300}
        componentsProps={FULL_VALUE_TOOLTIP_PROPS}
        title={<div className="db-preview-value-full">{text}</div>}
      >
        {inner}
      </Tooltip>
    );
  }

  return inner;
};

// Column name + type / PK / FK / constraints — shared by both layouts.
const ColumnMeta: React.FC<{ col: SchemaColumn }> = ({ col }) => {
  const hasDefault = col.defaultValue !== undefined && col.defaultValue !== null;
  return (
    <>
      <div className="db-preview-meta-name">
        <span className="db-preview-col-name">{col.name}</span>
        {col.primaryKey ? <span className="db-preview-pk">PK</span> : null}
        {col.foreignKey ? <span className="db-preview-fk">FK</span> : null}
      </div>
      {(col.type || col.nullable === false || hasDefault) && (
        <div className="db-preview-meta-type">
          {col.type ? <span className="db-preview-type">{col.type}</span> : null}
          {col.nullable === false ? <span className="db-preview-tag">· not null</span> : null}
          {hasDefault ? (
            <span className="db-preview-tag">· default {String(col.defaultValue)}</span>
          ) : null}
        </div>
      )}
    </>
  );
};

export const SourceTablePreview: React.FC<Props> = ({
  columns,
  samples,
  loading,
  tableLabel,
  canFetch,
  onFetch,
  mode,
  errorHint,
}) => {
  const [view, setView] = useState<'table' | 'json'>('table');
  // null = follow the column-count default; otherwise the user's explicit pick.
  const [userLayout, setUserLayout] = useState<Layout | null>(null);
  const [colWidth, setColWidth] = useState(DEFAULT_COL);
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);

  // Reset the layout override when the previewed table changes so the new table
  // gets its column-count-based default.
  useEffect(() => {
    setUserLayout(null);
  }, [tableLabel]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const s = dragRef.current;
      if (!s) return;
      setColWidth(Math.min(MAX_COL, Math.max(MIN_COL, s.startW + (e.clientX - s.startX))));
    };
    const onUp = () => {
      if (dragRef.current) {
        dragRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const startDrag = (e: React.MouseEvent) => {
    dragRef.current = { startX: e.clientX, startW: colWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const errorMessage = useMemo(() => {
    if (!samples) return null;
    const isError =
      (samples.errorCode && samples.errorCode !== 200) ||
      (samples.statusCode && samples.statusCode !== 200);
    if (!isError) return null;
    return samples.error || samples.errorMessage || 'There was an error fetching the data samples.';
  }, [samples]);

  const rows = samples?.response?.data ?? null;
  const sampleColumnNames = samples?.response?.columns ?? null;

  // Schema metadata keyed by lower-cased column name (DBs vary in case), for
  // type / PK / FK / nullable badges.
  const schemaByName = useMemo(() => {
    const map: Record<string, SchemaColumn> = {};
    columns.forEach((c) => {
      map[c.name.toLowerCase()] = c;
    });
    return map;
  }, [columns]);

  // Prefer the sample's column order when present (e.g. custom-query output),
  // enriching each with schema metadata when we recognize it; else the schema.
  const previewColumns: SchemaColumn[] = useMemo(() => {
    if (sampleColumnNames && sampleColumnNames.length) {
      return sampleColumnNames.map((name) => {
        const match = schemaByName[name.toLowerCase()];
        return match ? { ...match, name } : { name };
      });
    }
    return columns;
  }, [sampleColumnNames, schemaByName, columns]);

  const hasRows = !!rows && rows.length > 0;
  const colCount = previewColumns.length;
  const defaultLayout: Layout = colCount <= HORIZONTAL_MAX_COLS ? 'horizontal' : 'transposed';
  const layout: Layout = userLayout ?? defaultLayout;

  // `inflow` renders below the content (used by the horizontal grid so the card
  // clears the sticky header); otherwise it's an absolute overlay (transposed).
  const renderEmptyState = ({
    style,
    inflow,
  }: {
    style?: React.CSSProperties;
    inflow?: boolean;
  }) => (
    <div className={cx('db-preview-empty', { 'db-preview-empty--inflow': inflow })} style={style}>
      {mode === 'Query' ? (
        <EmptyView title="Run the query to preview results." />
      ) : (
        <EmptyView
          title="No sample rows fetched yet."
          actionTitle="Fetch sample"
          onClick={onFetch}
          actionProps={{ disabled: !canFetch, startIcon: <DownloadIcon /> }}
        />
      )}
    </div>
  );

  // Transposed: each column is a row; sample records run across.
  const renderTransposed = () => (
    <div className="db-preview-table-wrap">
      <table className={cx('db-preview-table', { 'is-empty': !hasRows })}>
        <tbody>
          {previewColumns.map((col) => (
            <tr key={col.name} className="db-preview-row">
              <th
                scope="row"
                style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                className="db-preview-meta"
              >
                <ColumnMeta col={col} />
              </th>
              {hasRows
                ? rows!.map((r, i) => (
                    <td key={i} className="db-preview-value">
                      <PreviewCell value={r[col.name]} />
                    </td>
                  ))
                : loading
                  ? Array.from({ length: SKELETON_TRANSPOSED_COLS }).map((_, i) => (
                      <td key={i} className="db-preview-value">
                        <span
                          className="db-preview-skeleton"
                          style={{ width: 56 + ((col.name.length * 7 + i * 13) % 70) }}
                        />
                      </td>
                    ))
                  : null}
            </tr>
          ))}
        </tbody>
      </table>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize column"
        className="db-preview-resize"
        style={{ left: colWidth }}
        onMouseDown={startDrag}
      />

      {!hasRows && !loading && renderEmptyState({ style: { paddingLeft: colWidth } })}
    </div>
  );

  // Horizontal: standard grid — columns across the top, records down.
  const renderHorizontal = () => (
    <div className="db-preview-grid-wrap">
      <table className="db-preview-grid">
        <thead>
          <tr>
            {previewColumns.map((col) => (
              <th key={col.name} className="db-preview-grid-th">
                <ColumnMeta col={col} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {hasRows
            ? rows!.map((r, ri) => (
                <tr key={ri}>
                  {previewColumns.map((col) => (
                    <td key={col.name} className="db-preview-grid-td">
                      <PreviewCell value={r[col.name]} />
                    </td>
                  ))}
                </tr>
              ))
            : loading
              ? Array.from({ length: SKELETON_HORIZONTAL_ROWS }).map((_, ri) => (
                  <tr key={ri}>
                    {previewColumns.map((col, ci) => (
                      <td key={col.name} className="db-preview-grid-td">
                        <span
                          className="db-preview-skeleton"
                          style={{ width: 50 + ((col.name.length * 7 + ri * 13 + ci * 5) % 80) }}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              : null}
        </tbody>
      </table>

      {!hasRows && !loading && renderEmptyState({ inflow: true })}
    </div>
  );

  return (
    <div className="db-preview">
      <div className="db-preview-header">
        <div className="db-preview-id">
          <span className="db-preview-badge">Preview</span>
          {tableLabel ? <span className="db-preview-label">{tableLabel}</span> : null}
          <span className="db-preview-meta-info">
            {colCount > 0 ? `· ${colCount} ${colCount === 1 ? 'col' : 'cols'}` : null}
            {hasRows ? ` · ${rows!.length} ${rows!.length === 1 ? 'row' : 'rows'}` : null}
          </span>
        </div>

        {colCount > 0 && (
          <div className="db-preview-controls">
            {view === 'table' && (
              <SegmentedControl
                value={layout}
                defaultValue="horizontal"
                iconOnly
                sx={PREVIEW_TOGGLE_SX}
                controls={LAYOUT_CONTROLS}
                onControlChange={(_e, value) => setUserLayout(value as Layout)}
              />
            )}

            {hasRows && (
              <SegmentedControl
                value={view}
                defaultValue="table"
                iconPosition="start"
                sx={PREVIEW_TOGGLE_SX}
                controls={VIEW_CONTROLS}
                onControlChange={(_e, value) => setView(value as 'table' | 'json')}
              />
            )}

            {hasRows && (
              <button
                type="button"
                className="db-preview-refresh"
                onClick={onFetch}
                disabled={!canFetch}
              >
                <RefreshIcon className={cx({ 'is-spinning': loading })} /> Refresh
              </button>
            )}
          </div>
        )}
      </div>

      {hasRows && view === 'table' && layout === 'transposed' && (
        <div className="db-preview-orient">
          <span className="db-preview-orient-col" style={{ width: colWidth }}>
            Columns ↓
          </span>
          <span className="db-preview-orient-rec">Records →</span>
        </div>
      )}

      <div className="db-preview-body">
        {errorMessage ? (
          errorHint ? (
            <div className="db-preview-error-hint">{errorHint}</div>
          ) : (
            <div className="db-preview-error">{errorMessage}</div>
          )
        ) : colCount === 0 ? (
          <div className="db-preview-empty">
            <div className="db-preview-empty-text">
              {loading || tableLabel
                ? 'Loading schema…'
                : 'Select a table to preview its schema and data.'}
            </div>
          </div>
        ) : view === 'json' && hasRows ? (
          <div className="db-preview-json-wrap">
            <JSONView json={JSON.stringify(rows, null, 2)} theme="dark" />
          </div>
        ) : layout === 'horizontal' ? (
          renderHorizontal()
        ) : (
          renderTransposed()
        )}
      </div>
    </div>
  );
};
